/**
 * Build two maps (long->short and short->long) from the given shortenUrlParamList,
 * @param {string[]} shortenUrlParamList - The list of mapping strings.
 * @returns {{ shortParamsMap: Map<string, string>, longParamsMap: Map<string, string> }}
 * @type {import('./index.d').buildShortLongMaps}
 */
export function buildShortLongMaps(shortenUrlParamList = []) {
  const shortParamsMap = new Map();
  const longParamsMap = new Map();

  shortenUrlParamList.forEach((rule) => {
    if (typeof rule !== 'string') return;
    const idx = rule.lastIndexOf(':');
    if (idx === -1) return;

    const longKey = rule.slice(0, idx).trim();
    const shortKey = rule.slice(idx + 1).trim();

    if (!longKey || !shortKey) return;

    shortParamsMap.set(longKey, shortKey);
    longParamsMap.set(shortKey, longKey);
  });

  return { shortParamsMap, longParamsMap };
}

/**
 * Converts keys of the given object from their short form to the corresponding long form using the provided longParamsMap.
 * @param {Object} obj - The object whose keys are in short form.
 * @param {Map<string, string>} longParamsMap - A Map that maps short keys to long keys.
 * @returns {Object} A new object with keys converted to their long forms.
 * @type {import('./index.d').shortToLongParams}
 */
export function shortToLongParams(obj, longParamsMap) {
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (longParamsMap.has(key)) {
      const longKey = longParamsMap.get(key);
      if (typeof longKey === 'string' && longKey.trim() !== '') result[longKey] = value;
    } else {
      if (typeof key === 'string') result[key] = value;
    }
  }
  return result;
}

/**
 * Converts keys of the given object from their long form to the corresponding short form using the provided shortParamsMap.
 * @param {Object} obj - The object whose keys are in long form.
 * @param {Map<string, string>} shortParamsMap - A Map that maps long keys to short keys.
 * @returns {Object} A new object with keys converted to their short forms.
 * @type {import('./index.d').longToShortParams}
 */
export function longToShortParams(obj, shortParamsMap) {
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (shortParamsMap.has(key)) {
      const shortKey = shortParamsMap.get(key);
      if (typeof shortKey === 'string') result[shortKey] = value;
    } else {
      result[key] = value;
    }
  }
  return result;
}
